import React, { useCallback, useMemo, useState } from "react";
import Button from "../../ui/Button";
import MenuSelect from "../../ui/MenuSelect";
import {
  ATTACH_ATTR,
  TYPE_OPTIONS,
  getAttachmentOptions,
  getSensorAttachType,
} from "./sensorTaxonomy";

const AddSensorForm = React.memo(
  ({ sites, joints, actuators, bodies, tendons, worldBodies, bodyRootMap, onAdd, onCancel }) => {
    const [kind, setKind] = useState("gyro");
    const [attachValue, setAttachValue] = useState("");
    const [name, setName] = useState("gyro_sensor");

    const attachType = getSensorAttachType(kind);

    const attachOptions = useMemo(() => {
      return getAttachmentOptions(attachType, {
        sites,
        joints,
        actuators,
        bodies,
        tendons,
        worldBodies,
        bodyRootMap,
      });
    }, [attachType, sites, joints, actuators, bodies, tendons, worldBodies, bodyRootMap]);

    const hasAttachOptions = !attachType || attachOptions.length > 0;
    const emptyAttachMessage =
      attachType === "site"
        ? "Add a site to a body before creating a site-mounted sensor."
        : attachType
          ? `Add a ${attachType} before creating this sensor.`
          : "";

    const handleKindChange = useCallback((newKind) => {
      setKind(newKind);
      setAttachValue("");
      setName(`${newKind}_sensor`);
    }, []);

    const handleAttachChange = useCallback(
      (val) => {
        setAttachValue(val);
        const selected = attachOptions.find((option) => option.value === val);
        setName(`${kind}_${selected?.label || val}`);
      },
      [attachOptions, kind]
    );

    const handleAdd = useCallback(() => {
      if (!name.trim()) return;
      const attrs = {};
      if (attachType && attachValue) attrs[ATTACH_ATTR[attachType]] = attachValue;
      onAdd({ kind, name: name.trim(), attrs });
    }, [kind, name, attachType, attachValue, onAdd]);

    const canAdd = Boolean(name.trim() && (!attachType || (hasAttachOptions && attachValue)));

    return (
      <div className="mx-2 mb-2 space-y-2 rounded border border-gray-600 bg-gray-800/60 px-3 py-2.5">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="mb-1 text-[11px] font-medium text-gray-300">Type</div>
            <MenuSelect
              value={kind}
              onChange={handleKindChange}
              options={TYPE_OPTIONS}
              size="compact"
            />
          </div>
          {attachType && (
            <div>
              <div className="mb-1 text-[11px] font-medium text-gray-300 capitalize">
                {attachType}
              </div>
              <MenuSelect
                value={attachValue}
                onChange={handleAttachChange}
                options={attachOptions}
                placeholder={hasAttachOptions ? `Select ${attachType}...` : `No ${attachType}s`}
                disabled={!hasAttachOptions}
                size="compact"
              />
              {!hasAttachOptions ? (
                <div className="mt-1 text-[11px] leading-4 text-amber-300">
                  {emptyAttachMessage}
                </div>
              ) : null}
            </div>
          )}
        </div>
        <div>
          <div className="mb-1 text-[11px] font-medium text-gray-300">Name</div>
          <input
            type="text"
            className="w-full rounded border border-gray-600 bg-gray-800 px-2 py-1 text-xs text-gray-200 focus:border-gray-400 focus:outline-none"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && canAdd) handleAdd();
              if (e.key === "Escape") onCancel();
            }}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="transparent" size="small" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" size="small" onClick={handleAdd} disabled={!canAdd}>
            Add
          </Button>
        </div>
      </div>
    );
  }
);

export default AddSensorForm;
